import React from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { Seo } from '../shared';
import KnowledgeHubLayout from '../explore/KnowledgeHubLayout';
import DiscoverySection from '../explore/DiscoverySection';
import OfficialCitation from '../explore/OfficialCitation';
import { getStageBySlug } from '../../data/lifecycleKnowledge';
import { LAST_VERIFIED } from '../../data/officialSources';
import {
	LIFECYCLE_TOURIST_REDIRECTS,
	planStepIdFromLifecycleSlug,
} from '../../data/touristNavigation';
import '../../styles/explore.scss';
import '../../styles/guides.scss';
import '../../styles/tourist-nav.scss';

function LifecycleHubPage() {
	const { stageSlug } = useParams();
	const stage = getStageBySlug(stageSlug);

	if (!stage) {
		return <Redirect to="/#plan" />;
	}

	if (stage.dedicated) {
		return <Redirect to={stage.route} />;
	}

	const touristRedirect = LIFECYCLE_TOURIST_REDIRECTS[stage.slug];
	if (touristRedirect) {
		return <Redirect to={touristRedirect} />;
	}

	const planStepId = planStepIdFromLifecycleSlug(stage.slug);
	const showPlanBooking = stage.slug === 'packing' || stage.slug === 'insurance';

	return (
		<KnowledgeHubLayout
			touristSection="plan"
			touristCurrent={stage.name}
			planStepId={planStepId}
			hideRelated
			planBookingPanel={showPlanBooking}
			seo={(
				<Seo
					title={`${stage.name} — Nepal Travel Guide`}
					path={`/plan/${stage.slug}`}
					description={`${stage.question} Practical Nepal travel guide for first-time visitors.`}
				/>
			)}
			hero={{
				image: stage.hero,
				eyebrow: 'Plan your trip',
				title: stage.name,
				lead: stage.lead,
			}}
			quickFacts={stage.quickFacts}
			intro={{
				title: stage.question,
				text: 'Read this page top to bottom — then use Next below to continue in order.',
				body: 'Official rules are cited where they apply. Always confirm fees and regulations on government sites before you travel.',
			}}
			faqs={stage.faqs}
		>
			<DiscoverySection eyebrow="Guide" title={stage.question}>
				{(stage.sections || []).map((s, i) => (
					<div key={s.id} id={s.id} className="info-card arrival-step-card" style={{ marginBottom: '1.25rem' }}>
						<div className="info-card__body">
							<span className="arrival-step-card__num">Section {i + 1}</span>
							<h3 className="info-card__title">{s.question}</h3>
							<p className="info-card__text">{s.answer}</p>
							{s.tips && s.tips.length > 0 && (
								<div className="arrival-tips">
									<p className="arrival-tips__label">Traveler tips</p>
									<ul>{s.tips.map((t) => <li key={t}>{t}</li>)}</ul>
								</div>
							)}
							{s.source && (
								<p className="section-source-tag">
									Official: <a href={s.source.url} target="_blank" rel="noopener noreferrer">{s.source.name}</a>
								</p>
							)}
						</div>
					</div>
				))}
			</DiscoverySection>

			<DiscoverySection eyebrow="Sources" title="How we verify this" alt>
				<OfficialCitation
					sourceName="VisitAllNepal Editorial"
					sourceUrl={null}
					lastVerified={LAST_VERIFIED}
					subjectToChange
				>
					<p className="mb-0">Regulatory content cites official sources. Practical sections are updated periodically — confirm current fees and rules before travel.</p>
				</OfficialCitation>
			</DiscoverySection>
		</KnowledgeHubLayout>
	);
}

export default LifecycleHubPage;
